import { memo, useCallback, useEffect, useMemo } from 'react';
import * as L from 'leaflet';
import {
  Circle,
  CircleMarker,
  ImageOverlay,
  MapContainer,
  Marker,
  Pane,
  Popup,
  TileLayer,
  useMapEvents,
} from 'react-leaflet';
import { altitudeColor, formatAlt, type Aircraft } from '@/lib/aircraft';
import type { SatPosition } from '@/lib/satellites';
import { NIGHT_OVERLAY_BOUNDS, type BodyState } from '@/lib/astro';
import { clampLat, formatDD, normLon, type GeoPoint } from '@/lib/geo';
import type { MapView } from '@/hooks/useAircraft';

export interface LayerState {
  planes: boolean;
  sats: boolean;
  astro: boolean;
  night: boolean;
}

export interface FlyTarget {
  point: GeoPoint;
  zoom: number;
  nonce: number;
}

interface SkyMapProps {
  initialCenter: GeoPoint;
  initialZoom: number;
  location: GeoPoint;
  onLocationChange: (p: GeoPoint) => void;
  onViewChange: (v: MapView) => void;
  flyTo: FlyTarget | null;
  tileStyle: 'dark' | 'osm';
  layers: LayerState;
  aircraft: Aircraft[];
  satellites: SatPosition[];
  bodies: BodyState[];
  nightUrl: string;
  selectedSat: string | null;
  onSelectSat: (norad: string | null) => void;
  selectedPlane: string | null;
  onSelectPlane: (hex: string | null) => void;
}

/* ---------- Map event / fly controller ---------- */

function MapController({
  onLocationChange,
  onViewChange,
  flyTo,
}: {
  onLocationChange: (p: GeoPoint) => void;
  onViewChange: (v: MapView) => void;
  flyTo: FlyTarget | null;
}) {
  const map = useMapEvents({
    click: (e) => {
      onLocationChange({ lat: clampLat(e.latlng.lat), lon: normLon(e.latlng.lng) });
    },
  });

  const emit = useCallback(() => {
    const c = map.getCenter();
    const b = map.getBounds();
    const radiusNm = Math.min(250, Math.max(25, c.distanceTo(b.getNorthEast()) / 1852));
    onViewChange({
      lat: Math.round(c.lat * 4) / 4,
      lon: Math.round(c.lng * 4) / 4,
      radiusNm: Math.max(25, Math.round(radiusNm / 5) * 5),
    });
  }, [map, onViewChange]);

  // Keep Leaflet's internal size in sync with the actual container size.
  // Without this, a container that resizes after init (mobile URL bar,
  // late layout settling) leaves tile-less black regions and off-center
  // flyTo targets.
  useEffect(() => {
    const invalidate = () => map.invalidateSize({ animate: false });
    const raf = requestAnimationFrame(invalidate);
    const timers = [100, 400, 1000].map((ms) =>
      setTimeout(() => {
        invalidate();
        emit();
      }, ms),
    );
    const ro = new ResizeObserver(invalidate);
    ro.observe(map.getContainer());
    window.addEventListener('orientationchange', invalidate);
    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
      ro.disconnect();
      window.removeEventListener('orientationchange', invalidate);
    };
  }, [map, emit]);

  useEffect(() => {
    emit();
    map.on('moveend', emit);
    return () => {
      map.off('moveend', emit);
    };
  }, [map, emit]);

  useEffect(() => {
    if (flyTo) {
      map.invalidateSize({ animate: false });
      map.flyTo([flyTo.point.lat, flyTo.point.lon], flyTo.zoom, { duration: 1.4 });
    }
  }, [flyTo, map]);

  return null;
}

/* ---------- Icons ---------- */

const locationIcon = L.divIcon({
  className: '',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
  html: '<div class="sky-crosshair"></div>',
});

const astroIconCache = new Map<string, L.DivIcon>();

function astroIcon(b: BodyState): L.DivIcon {
  const cached = astroIconCache.get(b.name);
  if (cached) return cached;
  const w = 36;
  const icon = L.divIcon({
    className: '',
    iconSize: [w, b.size + 20],
    iconAnchor: [w / 2, b.size / 2],
    popupAnchor: [0, -b.size / 2 - 2],
    html: `<div class="sky-marker" style="width:${w}px"><div class="sky-marker-dot" style="width:${b.size}px;height:${b.size}px;background:${b.color};box-shadow:0 0 ${b.size + 4}px 3px ${b.color}77"></div><div class="sky-marker-label">${b.abbr}</div></div>`,
  });
  astroIconCache.set(b.name, icon);
  return icon;
}

const PLANE_PATH =
  'M12 1.8c-.7 0-1.2.6-1.2 1.4v4.6l-8.2 3.2c-.4.15-.6.5-.6.9v1.3c0 .35.35.55.65.45l7.75-2.1v5.3l-2.6 2c-.3.25-.45.6-.45.95v.85c0 .35.35.55.7.45l3.95-1.05 3.95 1.05c.35.1.7-.1.7-.45v-.85c0-.35-.15-.7-.45-.95l-2.6-2v-5.3l7.75 2.1c.3.1.65-.1.65-.45v-1.3c0-.4-.2-.75-.6-.9l-8.2-3.2V3.2c0-.8-.5-1.4-1.2-1.4z';

function planeIcon(ac: Aircraft): L.DivIcon {
  const color = altitudeColor(ac.altBaro);
  const rot = Math.round(ac.track ?? 0);
  return L.divIcon({
    className: '',
    iconSize: [20, 20],
    iconAnchor: [10, 10],
    popupAnchor: [0, -10],
    html: `<svg class="sky-plane" style="transform:rotate(${rot}deg)" width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="${PLANE_PATH}" fill="${color}" stroke="#0b1020" stroke-width="0.8" stroke-linejoin="round"/></svg>`,
  });
}

/* ---------- Aircraft marker (memoized icon) ---------- */

const AircraftMarker = memo(function AircraftMarker({
  ac,
  selected,
  onSelect,
}: {
  ac: Aircraft;
  selected: boolean;
  onSelect: (hex: string | null) => void;
}) {
  const icon = useMemo(() => planeIcon(ac), [ac.track, ac.altBaro]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <Marker
        position={[ac.lat, ac.lon]}
        icon={icon}
        zIndexOffset={selected ? 500 : 100}
        eventHandlers={{ click: () => onSelect(selected ? null : ac.hex) }}
      >
        <Popup>
          <div className="min-w-44 space-y-1">
            <div className="font-display text-sm font-semibold">
              {ac.flight || ac.hex.toUpperCase()}
            </div>
            <div className="text-xs text-muted-foreground">
              {[ac.type, ac.reg].filter(Boolean).join(' · ') || 'Aircraft'}
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
              <span className="text-muted-foreground">Altitude</span>
              <span>{formatAlt(ac.altBaro)}</span>
              <span className="text-muted-foreground">Speed</span>
              <span>{ac.gs != null ? `${Math.round(ac.gs)} kt` : '—'}</span>
              <span className="text-muted-foreground">Heading</span>
              <span>{ac.track != null ? `${Math.round(ac.track)}°` : '—'}</span>
              <span className="text-muted-foreground">Squawk</span>
              <span>{ac.squawk ?? '—'}</span>
              <span className="text-muted-foreground">ICAO24</span>
              <span className="font-mono">{ac.hex}</span>
            </div>
          </div>
        </Popup>
      </Marker>
      {selected && (
        <CircleMarker
          center={[ac.lat, ac.lon]}
          radius={13}
          interactive={false}
          pathOptions={{ color: '#fbbf24', weight: 1.5, fill: false, dashArray: '4 4' }}
        />
      )}
    </>
  );
});

/* ---------- Main map ---------- */

export function SkyMap({
  initialCenter,
  initialZoom,
  location,
  onLocationChange,
  onViewChange,
  flyTo,
  tileStyle,
  layers,
  aircraft,
  satellites,
  bodies,
  nightUrl,
  selectedSat,
  onSelectSat,
  selectedPlane,
  onSelectPlane,
}: SkyMapProps) {
  const canvasRenderer = useMemo(() => L.canvas({ padding: 0.5 }), []);

  const selectedSatPos = useMemo(
    () => satellites.find((s) => s.norad === selectedSat) ?? null,
    [satellites, selectedSat],
  );

  return (
    <MapContainer
      center={[initialCenter.lat, initialCenter.lon]}
      zoom={initialZoom}
      minZoom={2}
      maxZoom={18}
      maxBounds={[
        [-85, -180],
        [85, 180],
      ]}
      maxBoundsViscosity={0.85}
      className={`absolute inset-0 h-full w-full ${tileStyle === 'dark' ? 'map-tiles-dark' : 'map-tiles-light'}`}
    >
      <TileLayer
        key={tileStyle}
        url={
          tileStyle === 'dark'
            ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png'
            : 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'
        }
        subdomains={tileStyle === 'dark' ? 'abcd' : 'abc'}
        maxZoom={19}
        updateWhenIdle={false}
        keepBuffer={tileStyle === 'dark' ? 4 : 3}
        noWrap
        attribution={
          tileStyle === 'dark'
            ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }
      />

      <MapController
        onLocationChange={onLocationChange}
        onViewChange={onViewChange}
        flyTo={flyTo}
      />

      {/* Night shading lives in its own pane below all vector layers/markers,
          so it never covers satellites, aircraft or zenith markers. */}
      <Pane name="night-pane" style={{ zIndex: 350 }}>
        {layers.night && nightUrl && (
          <ImageOverlay url={nightUrl} bounds={NIGHT_OVERLAY_BOUNDS} interactive={false} />
        )}
      </Pane>

      {layers.astro &&
        bodies.map((b) => (
          <Marker
            key={b.name}
            position={[b.sub.lat, b.sub.lon]}
            icon={astroIcon(b)}
            zIndexOffset={-100}
          >
            <Popup>
              <div className="space-y-0.5">
                <div className="font-display text-sm font-semibold">{b.name} zenith</div>
                <div className="text-xs text-muted-foreground">
                  The {b.name} is directly overhead at this point right now.
                </div>
                <div className="font-mono text-xs">{formatDD(b.sub)}</div>
              </div>
            </Popup>
          </Marker>
        ))}

      {layers.sats &&
        satellites.map((s) => (
          <CircleMarker
            key={s.norad}
            center={[s.lat, s.lon]}
            radius={s.isIss ? 7 : 4.5}
            renderer={canvasRenderer}
            pathOptions={{
              color: '#ffffff',
              weight: 1.4,
              opacity: s.isIss ? 0.95 : s.aboveHorizon ? 0.8 : 0.55,
              fillColor: s.isIss ? '#fb923c' : s.aboveHorizon ? '#0ea5e9' : '#8b99ad',
              fillOpacity: s.isIss ? 1 : s.aboveHorizon ? 0.95 : 0.7,
            }}
            eventHandlers={{ click: () => onSelectSat(selectedSat === s.norad ? null : s.norad) }}
          >
            <Popup>
              <div className="min-w-44 space-y-1">
                <div className="font-display text-sm font-semibold">{s.name}</div>
                <div className="text-xs text-muted-foreground">NORAD {s.norad}</div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                  <span className="text-muted-foreground">Altitude</span>
                  <span>{s.heightKm.toFixed(0)} km</span>
                  <span className="text-muted-foreground">Velocity</span>
                  <span>{s.velocityKms.toFixed(2)} km/s</span>
                  <span className="text-muted-foreground">Footprint</span>
                  <span>⌀ {(s.footprintKm * 2).toFixed(0)} km</span>
                  {Number.isFinite(s.el) && (
                    <>
                      <span className="text-muted-foreground">From you</span>
                      <span>
                        {s.el > 0 ? `+${s.el.toFixed(0)}° up` : `${s.el.toFixed(0)}° (below horizon)`}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </Popup>
          </CircleMarker>
        ))}

      {selectedSatPos && (
        <>
          <Circle
            center={[selectedSatPos.lat, selectedSatPos.lon]}
            radius={selectedSatPos.footprintKm * 1000}
            interactive={false}
            pathOptions={{
              color: '#fb7185',
              weight: 1.5,
              dashArray: '6 6',
              fillColor: '#fb7185',
              fillOpacity: 0.05,
            }}
          />
          <CircleMarker
            center={[selectedSatPos.lat, selectedSatPos.lon]}
            radius={8}
            interactive={false}
            pathOptions={{ color: '#fb7185', weight: 2, fill: false }}
          />
        </>
      )}

      {layers.planes &&
        aircraft.map((ac) => (
          <AircraftMarker
            key={ac.hex}
            ac={ac}
            selected={selectedPlane === ac.hex}
            onSelect={onSelectPlane}
          />
        ))}

      <Marker
        position={[location.lat, location.lon]}
        icon={locationIcon}
        interactive={false}
        keyboard={false}
        zIndexOffset={600}
      />
    </MapContainer>
  );
}
